import { FC, useEffect, useState, useCallback } from 'react';

import dynamic from 'next/dynamic';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { useMediaQuery } from '@mui/material';

import lazyHydrate from 'next-lazy-hydrate';
import { useQuery } from 'react-query';
import { Element, scroller } from 'react-scroll';
import { useRecoilValue } from 'recoil';

import { useWindowSize } from '@hooks_common/useWindowSize';

import { getSaleCasesList } from '@hooks_iqfu/public/useSaleCase';

import { getCompaniesPageMetaInfo } from '@utils_iqfu/public/getCompaniesPageMetaInfo';
import { isInCompanyPropertyTypePage } from '@utils_iqfu/public/isInCompanyPropertyTypePage';

import BreadCrumbs from '@components_iqfu/public/bread_crumbs/BreadCrumbs';
import CompanyCardsCarousel from '@components_iqfu/public/company_cards_carousel/CompanyCardsCarousel';
//※CTAは主要以外の検索軸でファーストビューになるので、lazy-hydrateしない
import PrimaryCTA from '@components_iqfu/public/cta/PrimaryCTA';
import FloatingCV from '@components_iqfu/public/floating_cv/floating_cv';
import IkukoSectionTitleWrapper from '@components_iqfu/public/ikuko_section_title_wrapper/IkukoSectionTitleWrapper';
import PageMainLead from '@components_iqfu/public/page_main_lead/PageMainLead';
import PrefectureSelectMap from '@components_iqfu/public/prefecture_select_map/PrefectureSelectMap';
import PublicPageContainer from '@components_iqfu/public/public_page_container/PublicPageContainer';
import RenderOnScroll from '@components_iqfu/public/render_on_scroll/RenderOnScroll';
import SearchCompaniesStepper from '@components_iqfu/public/search_companies_stepper/SearchCompaniesStepper';
import SearchCompaniesWrapper from '@components_iqfu/public/search_companies_wrapper/SearchCompaniesWrapper';
import SearchLocalCompanies from '@components_iqfu/public/search_local_companies/SearchLocalCompanies';
import SectionLead from '@components_iqfu/public/section_lead/SectionLead';
import SectionLeadLeftAlign from '@components_iqfu/public/section_lead_left_align/SectionLeadLeftAlign';

import publicHeaderHeight from '@recoil_iqfu/atoms/public/publicHeaderHeight';

import { CompaniesListWithPrefectureId } from '@type-def_iqfu/public/AllCompaniesList';
import { MarketData, SalesStatsData } from '@type-def_iqfu/public/MarketData';
import { SaleCaseItem } from '@type-def_iqfu/public/SaleCaseItem';

import { DefaultIqraLatLong } from '@constants_common';
import { MediaQuery } from '@constants_common';
import { PublicPrefecturesWithAll } from '@constants_iqfu';
import styles from '@styles_iqfu/public/companies.module.scss';
import { CompaniesPageParameter } from 'src/iqfu/data/companiesPagesData';

const CompanyLocationsMap = dynamic(() => import('@components_iqfu/public/mapbox/CompanyLocationsMap'), {
  //ブラウザでしか動かないのでSSRしない
  ssr: false,
});

const SaleCaseArea = lazyHydrate(() => import('@components_iqfu/public/sale_case_area/SaleCaseArea'), {
  on: [
    ['scroll', () => document],
    ['delay', 500],
  ],
  compatibleMode: true,
});

const CheckPrice = lazyHydrate(() => import('@components_iqfu/public/check_price/CheckPrice'), {
  on: [
    ['scroll', () => document],
    ['delay', 500],
  ],
  compatibleMode: true,
});

const PrefecturesLinkList = lazyHydrate(() => import('@components_iqfu/public/prefectures_list/PrefecturesLinkList'), {
  on: [
    ['scroll', () => document],
    ['delay', 500],
  ],
  compatibleMode: true,
});

const PricePageLinks = lazyHydrate(() => import('@components_iqfu/public/price_page_links/PricePageLinks'), {
  on: [
    ['scroll', () => document],
    ['delay', 500],
  ],
  compatibleMode: true,
});

const MarketInfo = lazyHydrate(() => import('@components_iqfu/public/market_info/MarketInfo'), {
  on: [
    ['scroll', () => document],
    ['delay', 500],
  ],
  compatibleMode: true,
});

const Footer = lazyHydrate(() => import('@components_iqfu/public/footer/Footer'), {
  on: [
    ['scroll', () => document],
    ['delay', 500],
  ],
  compatibleMode: true,
});

const ArticlesForBeginner = lazyHydrate(
  () => import('@components_iqfu/public/articles_for_beginner/ArticlesForBeginner'),
  {
    on: [
      ['scroll', () => document],
      ['delay', 500],
    ],
    compatibleMode: true,
  },
);

const UsersVoice = lazyHydrate(() => import('@components_iqfu/public/users_voice/UsersVoice'), {
  on: [
    ['scroll', () => document],
    ['delay', 500],
  ],
  compatibleMode: true,
});

//jsonになるのでundefinedは不可
export type CompaniesCountryCommonProps = {
  allCompaniesList: CompaniesListWithPrefectureId[];
  pageRenderParameter: Omit<CompaniesPageParameter, 'prerender_pages_list'>;
  serverCachedSaleCasesList: SaleCaseItem[];
  marketData: MarketData | null;
  landSalesStats: SalesStatsData | null;
  houseSalesStats: SalesStatsData | null;
  mansionSalesStats: SalesStatsData | null;
};

const CompaniesCountryCommon: FC<CompaniesCountryCommonProps> = ({
  allCompaniesList,
  pageRenderParameter,
  serverCachedSaleCasesList,
  marketData,
  landSalesStats,
  houseSalesStats,
  mansionSalesStats,
}) => {
  const router = useRouter();
  const headerHeight = useRecoilValue(publicHeaderHeight);

  const [saleCasesData, setSaleCasesData] = useState(serverCachedSaleCasesList);
  const { data: updateSaleCasesData } = useQuery(
    ['CompanyCountrySaleCases', pageRenderParameter.sale_cases_query],
    () => getSaleCasesList({ limit: 20, property_type: pageRenderParameter.sale_cases_query }),
    {
      staleTime: 1000 * 60 * 5,
    },
  );
  useEffect(() => {
    if (updateSaleCasesData) {
      setSaleCasesData(updateSaleCasesData?.sale_cases);
    }
  }, [updateSaleCasesData]);

  useEffect(() => {
    if (router.query.position) {
      scroller.scrollTo(String(router.query.position), {
        duration: 250,
        smooth: true,
        offset: -headerHeight - 4,
      });
    }
  }, [router.query]);

  const queryTypeString = pageRenderParameter.query_type_string;
  const pageQueryType = pageRenderParameter.page_query_type;
  const pageLabel = pageRenderParameter.page_label;
  const { height: windowHeight, width: windowWidth } = useWindowSize();
  const isMobile = useMediaQuery(MediaQuery.lg);

  //allCompaniesListからcompanies0件のものを除外
  const companiesList = allCompaniesList.filter((prefecture) => prefecture.companies.length > 0);

  //最初のタイトルの画面上端からのY座標を取得
  //※mobileでここまでスクロールしたらフローティングを表示する
  const [showFloatingThreshold, setShowFloatingThreshold] = useState(0);
  const showFloatingRef = useCallback(
    async (node: HTMLHeadingElement) => {
      if (isMobile) {
        //同階層ページ遷移時に諸々のレンダリング完了してから取得しないと正しい値が取得できない
        await new Promise((resolve) => setTimeout(resolve, 100));
        setShowFloatingThreshold(node?.getBoundingClientRect().top + document.documentElement.scrollTop - windowHeight);
      }
    },
    [windowWidth, pageRenderParameter],
  );

  const { getCompaniesCountryMetaTitle, getCompaniesCountryMetaDescription, getCompaniesCountryBreadCrumbsData } =
    getCompaniesPageMetaInfo;

  const leadSentence = (queryTypeString: string, pageQueryType: string) => {
    switch (pageQueryType) {
      case 'land':
        return {
          first: `「イクラ不動産　全国本店」おすすめの${queryTypeString}不動産会社を、`,
          second: '土地の売却実績をベースとし、４７都道府県毎におすすめ順にTOP３まで掲載しています。',
        };
      case 'house':
        return {
          first: `「イクラ不動産　全国本店」おすすめの${queryTypeString}不動産会社を、`,
          second: '一戸建ての売却実績をベースとし、４７都道府県毎におすすめ順にTOP３まで掲載しています。',
        };
      case 'mansion':
        return {
          first: `「イクラ不動産　全国本店」おすすめの${queryTypeString}不動産会社を、`,
          second: 'マンションの売却実績をベースとし、４７都道府県毎におすすめ順にTOP３まで掲載しています。',
        };
      case 'purchase':
        return {
          first: `「イクラ不動産　全国本店」おすすめの${queryTypeString}不動産会社を、`,
          second: '買取実績をベースとし、４７都道府県毎におすすめ順にTOP３まで掲載しています。',
        };
      case 'divorce':
        return {
          first: `「イクラ不動産　全国本店」おすすめの${queryTypeString}不動産会社を、`,
          second: '離婚による売却実績や豊富な経験を基に４７都道府県毎におすすめ順にTOP３まで掲載しています。',
        };
      case 'inheritance':
        return {
          first: `「イクラ不動産　全国本店」おすすめの${queryTypeString}不動産会社を、`,
          second: '相続による売却実績や豊富な経験を基に４７都道府県毎におすすめ順にTOP３まで掲載しています。',
        };
      case 'leaseback':
        return {
          first: `「イクラ不動産　全国本店」おすすめの${queryTypeString}不動産会社を、`,
          second: '４７都道府県毎におすすめ順にTOP３まで掲載しています。',
        };
      case 'akiya':
        return {
          first: `「イクラ不動産　全国本店」おすすめの地元で空き家の売却が得意な地元の不動産会社を、`,
          second: '空き家の売却実績や豊富な経験を基に、４７都道府県毎におすすめ順にTOP３まで掲載しています。',
        };
      case 'sell_high':
        return {
          first: `「イクラ不動産　全国本店」おすすめの${queryTypeString}不動産会社を、`,
          second: '４７都道府県毎におすすめ順にTOP３まで掲載しています。',
        };
      case 'sell_fast':
        return {
          first: `「イクラ不動産　全国本店」おすすめの${queryTypeString}不動産会社を、`,
          second: '４７都道府県毎におすすめ順にTOP３まで掲載しています。',
        };
      case 'any_properties':
        return {
          first: `「イクラ不動産　全国本店」おすすめの${queryTypeString}不動産会社を、`,
          second: '４７都道府県毎におすすめ順にTOP３まで掲載しています。',
        };
      default:
        return {
          first: `「イクラ不動産　全国本店」おすすめの${queryTypeString}不動産会社を、`,
          second: '売却実績をベースとし、４７都道府県毎におすすめ順にTOP３まで掲載しています。',
        };
    }
  };

  const metaTitle = getCompaniesCountryMetaTitle(pageRenderParameter.query_type_string);

  const metaDescription = getCompaniesCountryMetaDescription(
    pageRenderParameter.query_type_string,
    pageRenderParameter.property_type_string,
  );

  const metaOGUrl = `${process.env.NEXT_PUBLIC_CLIENT_BASEPATH}/companies${
    pageRenderParameter.page_query_type ? '/' + pageRenderParameter.page_query_type : ''
  }`;

  return (
    <PublicPageContainer>
      <Head>
        <title key="title">{metaTitle}</title>
        <meta name="description" content={metaDescription} key="description" />
        <meta property="og:title" content={metaTitle} key={'og_title'} />
        <meta property="og:description" content={metaDescription} key={'og_description'} />
        <meta property="og:url" content={metaOGUrl} key={'og_url'} />
        <meta name="twitter:title" content={metaTitle} key={'twitter_title'} />
        <meta name="twitter:description" content={metaDescription} key={'twitter_description'} />
        <link rel="canonical" href={metaOGUrl} />
      </Head>
      <BreadCrumbs
        breadCrumbs={getCompaniesCountryBreadCrumbsData(
          pageRenderParameter.query_type_string,
          pageRenderParameter.page_query_type,
        )}
      />
      <>
        <div className={styles.page_container}>
          <div className={styles.top_title}>
            <SectionLead isH1Tag>
              【全国版】
              {queryTypeString}不動産屋さんランキング
            </SectionLead>
          </div>

          <PageMainLead>
            {leadSentence(queryTypeString, pageQueryType).first}
            <br className="pc_only" />
            {leadSentence(queryTypeString, pageQueryType).second}
          </PageMainLead>

          <div className={styles.search_companies_container}>
            <SearchCompaniesWrapper>
              {!pageQueryType && (
                <>
                  <SearchLocalCompanies pageLabel={pageLabel} location={'upper'} />
                  <SearchCompaniesStepper pageLabel={pageLabel} location={'upper'} />
                </>
              )}
              <div ref={showFloatingRef}>
                <div className={styles.search_companies_sectionTitle_container}>
                  <IkukoSectionTitleWrapper>
                    <span className="title_text_strong">「都道府県別」</span>
                    {queryTypeString}
                    地元の不動産屋さんランキングを見る
                  </IkukoSectionTitleWrapper>
                </div>

                <PrefecturesLinkList
                  category="company"
                  hasReadMoreButton={true}
                  pageQueryType={pageQueryType}
                  location={'upper'}
                />
              </div>
              {!pageQueryType && (
                <PricePageLinks pageLabel={pageLabel} areaInfo={{ areaName: '全国', areaType: 'country' }} />
              )}
            </SearchCompaniesWrapper>
          </div>

          <div className={`${styles.cta_container}`}>
            <PrimaryCTA inFirstView />
          </div>

          <div>
            <Element name="rank">
              <div className={styles.rank_section_title}>
                <SectionLeadLeftAlign>全国の{queryTypeString}おすすめの不動産屋 TOP3ランキング</SectionLeadLeftAlign>
              </div>
            </Element>
            {companiesList &&
              companiesList.length > 0 &&
              companiesList.map((prefecture_companies, i: number) => {
                return (
                  <div key={i}>
                    {i > 0 && i !== Math.floor(companiesList.length / 5) && i % 5 === 0 && (
                      <div className={`${styles.cta_container} ${styles.in_country_ranking}`}>
                        <PrimaryCTA hasTopPageLink />
                      </div>
                    )}
                    <div
                      className={`${styles.country_ranking_container} ${i % 2 === 1 ? styles.odd_rank : ''} ${
                        i === 0 ? styles.first_rank : ''
                      }`}
                    >
                      <h3 className={styles.rankingHeading}>
                        {PublicPrefecturesWithAll[prefecture_companies.prefecture_code]}の{queryTypeString}
                        <br className="sp_only" />
                        地元の不動産屋 {prefecture_companies.companies.length === 3 ? 'TOP3' : ''}ランキング
                      </h3>
                      <div className={styles.ranking_carousel_container}>
                        <CompanyCardsCarousel data={companiesList[i].companies} pageLabel={pageLabel} />
                      </div>
                      <Link
                        href={`/companies/prefectures/${prefecture_companies.prefecture_code}/${pageQueryType}`}
                        prefetch={false}
                        className={styles.prefecture_ranking_link}
                      >
                        <button className={styles.country_company_search_button}>
                          <div className={styles.country_company_search_inner}>
                            <div className={styles.recommend_ikuko_icon}>
                              <img src="/images/iqfu/public/search_companies_stepper/ikuko_search.svg" alt="" />
                            </div>
                            {PublicPrefecturesWithAll[prefecture_companies.prefecture_code]}の{queryTypeString}
                            <br className="sp_only" />
                            不動産屋さんランキング
                          </div>
                        </button>
                      </Link>
                    </div>
                  </div>
                );
              })}
          </div>

          <div className={styles.search_companies_container}>
            <SearchCompaniesWrapper hasShadow>
              <PricePageLinks pageLabel={pageLabel} areaInfo={{ areaName: '全国', areaType: 'country' }} />
              <SearchLocalCompanies pageLabel={pageLabel} location={'lower'} />
              <SearchCompaniesStepper pageLabel={pageLabel} hasRecommendButton location={'lower'} />
            </SearchCompaniesWrapper>
          </div>

          <div className={styles.check_price_container}>
            <CheckPrice
              pageLabel={pageLabel}
              searchOption={{
                area: 'country',
              }}
            />
          </div>

          <div className={styles.articles_for_beginner_container}>
            <ArticlesForBeginner pageLabel={pageLabel} />
          </div>

          <div className={`${styles.cta_container}`}>
            <PrimaryCTA hasTopPageLink />
          </div>

          <div className={styles.users_voice_container}>
            <UsersVoice />
          </div>

          <div className={styles.market_container}>
            <MarketInfo marketDataTitle={'全国の不動産売却の「動向」'} marketData={marketData} />
          </div>

          <div className={styles.sale_case_area_container}>
            <div className={styles.saleCase_lead_container}>
              <SectionLeadLeftAlign>【全国】直近の売却実績一覧</SectionLeadLeftAlign>
            </div>

            <SaleCaseArea
              saleCasesData={saleCasesData}
              pageLabel={pageLabel}
              showTableTitle={!isInCompanyPropertyTypePage(pageLabel)}
              onlyLandSaleCases={pageLabel.indexOf('land') !== -1}
            />
          </div>

          <div className={styles.map_area_container}>
            <div className={styles.map_lead_wrap}>
              <SectionLeadLeftAlign>イクラ不動産本店地図</SectionLeadLeftAlign>
            </div>

            <div className={styles.map_container}>
              <RenderOnScroll dependency={pageRenderParameter}>
                <CompanyLocationsMap
                  mapType="onlyCenterCompanyMap"
                  lat={DefaultIqraLatLong.lat}
                  long={DefaultIqraLatLong.long}
                  zoom={8}
                />
              </RenderOnScroll>
            </div>
          </div>

          <div className={styles.prefectures_link_list_container}>
            <div className={styles.japan_map_section_title}>
              <SectionLeadLeftAlign>「都道府県別」地元の不動産屋さんを探す・相場を調べる</SectionLeadLeftAlign>
            </div>
            <PrefectureSelectMap
              landSalesStats={landSalesStats}
              houseSalesStats={houseSalesStats}
              mansionSalesStats={mansionSalesStats}
            />
          </div>

          <div className={`${styles.cta_container} ${styles.no_margin_bottom}`}>
            <PrimaryCTA hasTopPageLink />
          </div>
        </div>

        <FloatingCV pageLabel={pageLabel} showFloatingThreshold={showFloatingThreshold} />
        <Footer />
      </>
    </PublicPageContainer>
  );
};

export default CompaniesCountryCommon;
